import { currentYear } from "@/lib/siteYear";
// SEO/AIO landing config for /lawn-mowers/category/:slug.
// Each subcategory has SEO metadata, intro copy, FAQs and a filter
// function that selects matching rows from public.lawn_mowers.

export interface LawnMowerRow {
  id: string;
  brand: string;
  model: string;
  slug: string;
  mower_type: string;        // push | self-propelled | ride-on | robotic | hover | cylinder
  power_type: string;        // petrol | cordless | corded | manual
  self_propelled?: boolean | null;
  cylinder?: boolean | null;
  robotic?: boolean | null;
  hover?: boolean | null;
  description?: string | null;
  cutting_width_cm?: number | null;
}

export interface LawnMowerSubcategoryConfig {
  slug: string;
  title: string;
  shortLabel: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  longCopy: string[];
  faqs: { q: string; a: string }[];
  filter: (m: LawnMowerRow) => boolean;
}

const Y = currentYear();

const matches = (m: LawnMowerRow, words: string[]) => {
  const hay = `${m.model} ${m.description ?? ""}`.toLowerCase();
  return words.some(w => hay.includes(w));
};

export const lawnMowerSubcategories: Record<string, LawnMowerSubcategoryConfig> = {
  "electric-lawn-mowers": {
    slug: "electric-lawn-mowers",
    title: "Electric Lawn Mowers",
    shortLabel: "Electric",
    h1: `Best Electric Lawn Mowers UK ${Y}`,
    metaTitle: `Best Electric Lawn Mowers UK ${Y} | Corded & Cordless Reviewed`,
    metaDescription:
      "Independent UK reviews of the best electric lawn mowers. Compare corded and cordless models for quiet, low-maintenance, eco-friendly mowing.",
    intro:
      "Electric lawn mowers are the quiet, low-maintenance choice for most UK gardens. Plug-in corded models start instantly and never run out of power, while cordless battery models give you full freedom from cables — perfect for gardens up to half an acre.",
    longCopy: [
      "Electric mowers cover two distinct camps: corded electric (mains-powered) and cordless battery. Corded mowers are the cheapest way to a clean cut on small lawns up to about 200 m², with no fuel, no fumes and no recharging.",
      "Cordless lawn mowers have come a long way thanks to 36V, 40V and twin-18V platforms from Bosch, Makita, EGO, DeWalt and Stihl. A 4–5Ah battery typically mows 250–400 m² on a single charge, with twin-battery models stretching that to 700 m² or more.",
      "Look for brushless motors, a steel deck on larger models, single-lever height adjustment and at least a 40-litre grass box. Mulching and rear-roller options are worth paying for if you want a striped, manicured finish.",
    ],
    faqs: [
      {
        q: "Are electric lawn mowers good for UK gardens?",
        a: "Yes — electric mowers suit the vast majority of UK gardens. Corded models handle lawns up to about 200 m², while modern 36V+ cordless mowers comfortably tackle 400–700 m² on a single charge.",
      },
      {
        q: "How long do electric lawn mower batteries last?",
        a: "A 4Ah battery typically mows for 30–45 minutes — enough for around 300 m² of lawn. Battery cells themselves usually last 500–1,000 charge cycles, or roughly 5–8 years of weekly mowing.",
      },
      {
        q: "Are electric mowers strong enough for long grass?",
        a: "Modern brushless cordless mowers easily handle weekly long-grass mowing. For very overgrown or wet lawns, choose a model with a 40V+ battery, steel deck and high-torque mode.",
      },
    ],
    filter: m => m.power_type === "corded" || m.power_type === "cordless",
  },

  "petrol-lawn-mowers": {
    slug: "petrol-lawn-mowers",
    title: "Petrol Lawn Mowers",
    shortLabel: "Petrol",
    h1: `Best Petrol Lawn Mowers UK ${Y}`,
    metaTitle: `Best Petrol Lawn Mowers UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "The best petrol lawn mowers in the UK reviewed and ranked. Compare push, self-propelled and rear-roller petrol mowers for medium to large gardens.",
    intro:
      "Petrol lawn mowers deliver maximum power for medium to large UK gardens. Unrestricted runtime, the torque to chew through long damp grass and the wider cutting decks you need for half an acre and beyond — all without a battery to recharge.",
    longCopy: [
      "Petrol mowers come in three main flavours: push, self-propelled and ride-on. Push petrol mowers from Mountfield, Webb and Honda give you a 40–46 cm deck for around £200–£400 — perfect for lawns up to 500 m².",
      "Self-propelled petrol mowers drive themselves, which makes a big difference on slopes or anything over about 700 m². Look for variable speed, electric start and a 50+ litre grass box on larger decks.",
      "Honda GCV, Briggs & Stratton DOV and Kawasaki FJ engines dominate the UK market. Honda is the quietest and cleanest-starting; Briggs is the cheapest to maintain; Kawasaki delivers commercial-grade torque.",
    ],
    faqs: [
      {
        q: "Are petrol lawn mowers still worth buying in the UK?",
        a: "Yes — for lawns over about 500 m² or anything with slopes, long damp grass or rough edges, petrol mowers still outperform most cordless models on power, runtime and cutting width.",
      },
      {
        q: "How much petrol does a lawn mower use?",
        a: "A typical 140cc push petrol mower uses around 0.8 litres of unleaded per hour. A self-propelled 160–190cc mower uses 1.0–1.4 litres per hour. Always use fresh E5 or E10 unleaded with a fuel stabiliser if storing.",
      },
      {
        q: "How long do petrol lawn mowers last?",
        a: "A well-maintained Honda or Briggs & Stratton-powered petrol mower will easily last 10–15 years of weekly mowing. Annual oil and air filter changes are essential.",
      },
    ],
    filter: m => m.power_type === "petrol" && m.mower_type !== "ride-on",
  },

  "cordless-lawn-mowers": {
    slug: "cordless-lawn-mowers",
    title: "Cordless Lawn Mowers",
    shortLabel: "Cordless",
    h1: `Best Cordless Lawn Mowers UK ${Y}`,
    metaTitle: `Best Cordless Lawn Mowers UK ${Y} | Battery Mowers Compared`,
    metaDescription:
      "Independent UK reviews of the best cordless (battery) lawn mowers. Bosch, Makita, EGO, DeWalt, Stihl and more compared on runtime, deck and value.",
    intro:
      "Cordless lawn mowers combine the convenience of electric with full freedom from cables. Modern 36V, 40V and twin-18V battery platforms now match the power of mid-range petrol mowers on lawns up to 700 m² — quietly, cleanly and with almost zero maintenance.",
    longCopy: [
      "Battery voltage is the headline number, but Ah (battery capacity) and motor type matter more in practice. A brushless 36V motor with a 4Ah battery is the sweet spot for most UK gardens — enough for around 350 m² per charge.",
      "Twin-battery 2x18V mowers from Makita, DeWalt and Bosch let you reuse batteries across your wider tool kit. EGO's 56V Arc Lithium platform is the most powerful consumer cordless system you can buy.",
      "Look for a brushless motor (longer life, more torque), a 6+ position height-adjust lever, mulching plug, and a 50-litre+ grass box on decks over 40 cm. Steel decks are worth the upgrade if you mow weekly.",
    ],
    faqs: [
      {
        q: "How big a garden can a cordless lawn mower cut?",
        a: "Most 36V/40V cordless mowers with a 4Ah battery cut 250–400 m² per charge. Twin-battery and 56V models stretch that to 600–800 m² — large enough for most UK suburban gardens.",
      },
      {
        q: "Are cordless mowers as powerful as petrol?",
        a: "On weekly mowing, modern brushless cordless mowers match a 140cc petrol push mower for cut quality. For very long, wet or thick grass on large lawns, petrol still has the edge on raw torque.",
      },
      {
        q: "Can I leave a cordless mower battery on charge?",
        a: "Modern lithium batteries have built-in cell protection so leaving them on the charger is safe, but for maximum lifespan store them at 50–80% charge in a cool, dry place between mowing seasons.",
      },
    ],
    filter: m => m.power_type === "cordless",
  },

  "ride-on-lawn-mowers": {
    slug: "ride-on-lawn-mowers",
    title: "Ride-On Lawn Mowers",
    shortLabel: "Ride-On",
    h1: `Best Ride-On Lawn Mowers UK ${Y}`,
    metaTitle: `Best Ride-On Lawn Mowers UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "Independent UK reviews of the best ride-on lawn mowers. Compare lawn riders, garden tractors and zero-turn mowers for lawns over half an acre.",
    intro:
      "Ride-on lawn mowers turn an exhausting half-day of mowing into an enjoyable hour. For gardens, paddocks and estates over half an acre, a ride-on is the only practical answer — and modern models from Husqvarna, Stiga, Cub Cadet and John Deere have never been better value.",
    longCopy: [
      "Ride-on mowers split into three categories: compact lawn riders (66–84 cm decks, gates-friendly), full-size garden tractors (92–122 cm decks, towing-capable), and zero-turn mowers (extreme manoeuvrability for complex layouts).",
      "Hydrostatic transmission is worth the upgrade — no clutch, infinitely variable speed and far easier to drive than manual gearbox models. Most quality ride-ons today are hydrostatic.",
      "Cutting deck size determines speed, not quality. A 92 cm deck mows a half-acre in about an hour; a 122 cm deck does it in 35 minutes. Match the deck width to your access points and your lawn area.",
    ],
    faqs: [
      {
        q: "What size garden needs a ride-on mower?",
        a: "Generally, gardens over half an acre (around 2,000 m²) justify a ride-on mower. Below that, a self-propelled walk-behind is usually faster and easier to store.",
      },
      {
        q: "How much does a ride-on lawn mower cost in the UK?",
        a: "Compact ride-ons start around £1,800–£2,500. Mid-range garden tractors with hydrostatic drive sit at £3,000–£4,500. Premium zero-turn mowers and large garden tractors run from £5,000 to £12,000+.",
      },
      {
        q: "Do I need a licence to drive a ride-on mower?",
        a: "No — you don't need a driving licence to use a ride-on mower on private land. You will need road tax and a licence if you drive one on a public road.",
      },
    ],
    filter: m => m.mower_type === "ride-on",
  },

  "garden-tractors": {
    slug: "garden-tractors",
    title: "Garden Tractors",
    shortLabel: "Garden Tractors",
    h1: `Best Garden Tractors UK ${Y}`,
    metaTitle: `Best Garden Tractors UK ${Y} | Lawn Tractor Reviews`,
    metaDescription:
      "The best garden tractors in the UK reviewed. Compare full-size lawn tractors with 92–122 cm decks, hydrostatic drive and towing capability for large gardens.",
    intro:
      "Garden tractors are the heavy-hitters of the ride-on world — full-size frames, 92–122 cm cutting decks, towing hitches and the muscle to handle paddocks, large lawns and groundskeeping work. Built for gardens of an acre and up.",
    longCopy: [
      "What separates a garden tractor from a compact lawn rider? Frame size, deck width, engine displacement and towing capacity. Garden tractors typically run 500cc+ V-twin engines, 92–122 cm decks and standard towing hitches for trailers and accessories.",
      "Hydrostatic transmission with cruise control is standard on quality garden tractors. Look for cast-iron front axles, twin-blade or triple-blade decks and grease-zerk pivot points for long-term durability.",
      "Top UK garden tractor brands include Husqvarna, John Deere, Cub Cadet, Stiga and Mountfield. Pair your tractor with attachments — collector, mulch kit, snow plough, scarifier or trailer — to make it a true all-season garden machine.",
    ],
    faqs: [
      {
        q: "What's the difference between a lawn tractor and a garden tractor?",
        a: "Lawn tractors are designed primarily for cutting grass and have lighter frames and smaller decks. Garden tractors have heavier frames, more powerful engines (often V-twin), wider decks and the strength to tow trailers, scarifiers and ploughs.",
      },
      {
        q: "Can a garden tractor pull a trailer?",
        a: "Yes — most garden tractors have a standard 1¼-inch hitch and can pull 200–400 kg of trailer load. Always check your model's towing limit and avoid towing on steep slopes.",
      },
      {
        q: "How long do garden tractors last?",
        a: "Well-maintained garden tractors with V-twin engines easily last 15–20 years. Service the engine, transmission and deck spindles annually for maximum lifespan.",
      },
    ],
    filter: m => m.mower_type === "ride-on" && (matches(m, ["tractor", "garden tractor"]) || (m.cutting_width_cm ?? 0) >= 90),
  },

  "zero-turn-lawn-mowers": {
    slug: "zero-turn-lawn-mowers",
    title: "Zero Turn Lawn Mowers",
    shortLabel: "Zero Turn",
    h1: `Best Zero Turn Lawn Mowers UK ${Y}`,
    metaTitle: `Best Zero Turn Lawn Mowers UK ${Y} | Zero-Turn Reviews`,
    metaDescription:
      "Independent UK reviews of the best zero turn lawn mowers. Compare consumer and commercial zero-turn ride-ons for fast, manoeuvrable mowing on complex lawns.",
    intro:
      "Zero turn lawn mowers can pivot on the spot, slashing mowing time on complex layouts with lots of trees, beds and obstacles. Dual independent hydrostatic transmissions controlled by twin lap-bars give you total manoeuvrability — once you get used to the steering.",
    longCopy: [
      "Zero-turn mowers (often abbreviated ZTR) use two independent hydrostatic transmissions, each driving one rear wheel. Push one lap-bar forward and the other back and the mower spins on its own footprint — the famous 'zero turn radius'.",
      "ZTRs are dramatically faster than steering-wheel ride-ons on lawns with lots of obstacles. Expect to mow about 30% more square metres per hour compared to an equivalent-deck garden tractor.",
      "Consumer ZTRs from Cub Cadet, Husqvarna and Toro start around £4,500 in the UK. Commercial Scag, Ferris and Walker zero-turns built for groundskeepers run from £8,000 to £20,000+.",
    ],
    faqs: [
      {
        q: "Are zero turn mowers good for slopes?",
        a: "Standard zero-turn mowers are limited to gentle slopes of around 10–15°. For anything steeper, choose an all-wheel-drive ZTR or stick with a steering-wheel garden tractor.",
      },
      {
        q: "Are zero turn mowers worth the extra money?",
        a: "If you have a complex lawn with lots of trees, flower beds or obstacles — yes. A zero-turn easily cuts mowing time by 25–35% versus a comparable garden tractor. On simple rectangular lawns, the speed advantage is smaller.",
      },
      {
        q: "How hard is it to learn to drive a zero turn?",
        a: "Most people pick up zero-turn lap-bar steering in 20–30 minutes. Start in an open area, keep both bars symmetrical for straight lines, and avoid sudden movements on slopes or near beds.",
      },
    ],
    filter: m => m.mower_type === "ride-on" && matches(m, ["zero turn", "zero-turn", "ztr"]),
  },

  "cylinder-lawn-mowers": {
    slug: "cylinder-lawn-mowers",
    title: "Cylinder Lawn Mowers",
    shortLabel: "Cylinder",
    h1: `Best Cylinder Lawn Mowers UK ${Y}`,
    metaTitle: `Best Cylinder Lawn Mowers UK ${Y} | Reel Mowers Reviewed`,
    metaDescription:
      "The best cylinder (reel) lawn mowers in the UK reviewed. Compare manual, electric and petrol cylinder mowers for a bowling-green stripe and precision cut.",
    intro:
      "Cylinder lawn mowers — also called reel mowers — deliver the scissor-precise cut you see on bowling greens, cricket squares and the finest formal lawns. If you want a striped, manicured finish, nothing beats a cylinder mower.",
    longCopy: [
      "Cylinder mowers cut grass with a scissoring action between a spinning helical cylinder and a fixed bottom blade — the cleanest cut possible on healthy lawns. The result is a sharper, healthier blade tip than any rotary mower can match.",
      "Manual push cylinder mowers from Allett and Webb cost £100–£250 and are perfect for small formal lawns up to 100 m². Petrol cylinder mowers from Allett, Hayter and Atco scale up to bowling-green standard, with prices from £600 to £2,500+.",
      "Cylinder mowers prefer regularly mown, even lawns. They struggle with long grass and uneven ground — so scarify, level and mow weekly to get the most from them. Most accept a rear roller, scarifier and brush cartridges for true all-season formal lawn care.",
    ],
    faqs: [
      {
        q: "Are cylinder mowers better than rotary mowers?",
        a: "For a striped, manicured finish on a maintained lawn — yes. Cylinder mowers cut cleaner, leave healthier blade tips and deliver the iconic bowling-green stripe. For long, uneven or weedy grass, a rotary mower is more practical.",
      },
      {
        q: "How often should you mow with a cylinder mower?",
        a: "Cylinder mowers work best on weekly mowing during the growing season — sometimes twice a week for very fine lawns. Never let the grass get long; cylinder mowers can't cut anything taller than about 6 cm cleanly.",
      },
      {
        q: "Do cylinder mowers stripe better than rotary mowers?",
        a: "Yes — almost all cylinder mowers have a rear roller as standard, which is what creates the stripe by bending the grass blades in alternating directions. Cylinder stripes are also sharper and more even.",
      },
    ],
    filter: m => m.mower_type === "cylinder" || m.cylinder === true,
  },

  "robotic-lawn-mowers": {
    slug: "robotic-lawn-mowers",
    title: "Robotic Lawn Mowers",
    shortLabel: "Robotic",
    h1: `Best Robotic Lawn Mowers UK ${Y}`,
    metaTitle: `Best Robotic Lawn Mowers UK ${Y} | Robomow & Husqvarna Reviewed`,
    metaDescription:
      "Independent UK reviews of the best robotic lawn mowers. Husqvarna Automower, Worx Landroid, EcoFlow Blade, Robomow and more compared.",
    intro:
      "Robotic lawn mowers cut your lawn on a schedule while you do anything else. Set the area, install the boundary wire (or rely on GPS/RTK on newer models), and let the robot trim a few millimetres every day for a perfectly maintained, mulched lawn.",
    longCopy: [
      "Robotic mowers come in two camps: traditional boundary-wire mowers (Husqvarna Automower, Worx Landroid, Bosch Indego) and wire-free GPS/RTK robots (EcoFlow Blade, Mammotion Luba, Husqvarna NERA EPOS). Wire-free models cost more but skip the messy install.",
      "Sizing is straightforward: pick a model rated for at least 20% more than your actual lawn area. Robotic mowers handle slopes of 25–45% depending on the model, and most are fully weather-proof — they happily mow through light rain.",
      "Run-time and battery costs are minimal — a typical robot uses 60–80 kWh of electricity per year, around £20 on a UK tariff. The mulch-as-you-go approach feeds the lawn naturally and eliminates grass disposal completely.",
    ],
    faqs: [
      {
        q: "Are robotic lawn mowers worth it in the UK?",
        a: "For lawns over 200 m², or anyone short on time, yes. Once installed, robotic mowers deliver a consistently neat finish with effectively zero ongoing effort — and they raise lawn quality because they mow little-and-often.",
      },
      {
        q: "Do robotic mowers work without a perimeter wire?",
        a: "Newer GPS and RTK-GPS models like the EcoFlow Blade and Mammotion Luba 2 work without a perimeter wire, using satellite positioning instead. Most other robots still rely on a buried boundary wire.",
      },
      {
        q: "Are robotic mowers safe for children and pets?",
        a: "Yes — modern robotic mowers stop their blades instantly on tilt, lift or impact. Many have PIN-code start, child-lock and apps for safe boundaries. Still, supervise small children and pets in the garden during mowing windows.",
      },
    ],
    filter: m => m.mower_type === "robotic" || m.robotic === true,
  },

  "self-propelled-lawn-mowers": {
    slug: "self-propelled-lawn-mowers",
    title: "Self-Propelled Lawn Mowers",
    shortLabel: "Self-Propelled",
    h1: `Best Self-Propelled Lawn Mowers UK ${Y}`,
    metaTitle: `Best Self-Propelled Lawn Mowers UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "The best self-propelled lawn mowers in the UK reviewed. Petrol, cordless and corded models compared for medium to large gardens and sloped lawns.",
    intro:
      "Self-propelled lawn mowers drive themselves forward — you just steer. They're the best choice for lawns over about 400 m², gardens with any slope, or anyone who wants to make weekly mowing physically easier.",
    longCopy: [
      "Self-propelled mowers come in front-wheel-drive, rear-wheel-drive and all-wheel-drive layouts. Front-wheel-drive is lightest and easiest to manoeuvre; rear-wheel-drive grips better on slopes; all-wheel-drive is the most capable on rough or uneven terrain.",
      "Variable speed control is worth paying for — being able to slow down for trimming around beds and speed up on the open lawn makes a huge difference. Electric start is the other upgrade most people thank themselves for after a year of weekly mowing.",
      "Top self-propelled mowers in the UK come from Honda, Mountfield, Hayter, Stiga and Webb on the petrol side, and EGO, Stihl, Bosch and Makita for cordless. Look for at least a 46 cm deck for lawns over 500 m².",
    ],
    faqs: [
      {
        q: "Do I need a self-propelled lawn mower?",
        a: "If your lawn is over 400 m², has slopes, or you find pushing a mower physically tiring — yes. Self-propelled mowers also make wider 46–53 cm decks practical, which speeds up larger lawns significantly.",
      },
      {
        q: "Are self-propelled mowers harder to maintain?",
        a: "Slightly — the drive system has a belt or cable that may need adjustment every couple of years, and on petrol models you have an extra grease point or two. Routine engine servicing is no different to a push mower.",
      },
      {
        q: "Front, rear or all-wheel-drive — which is best?",
        a: "For flat lawns, front-wheel-drive is lightest and easiest. For slopes, rear-wheel-drive grips better as weight transfers backward going uphill. For rough or hilly gardens, all-wheel-drive is the most capable.",
      },
    ],
    filter: m => m.self_propelled === true || m.mower_type === "self-propelled",
  },

  "push-lawn-mowers": {
    slug: "push-lawn-mowers",
    title: "Push Lawn Mowers",
    shortLabel: "Push",
    h1: `Best Push Lawn Mowers UK ${Y}`,
    metaTitle: `Best Push Lawn Mowers UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "Independent UK reviews of the best push lawn mowers. Compare manual, electric and petrol push mowers for small and medium gardens.",
    intro:
      "Push lawn mowers are the simplest, most affordable way to keep a small or medium garden trim. Lightweight, easy to store and quick to start, push mowers cover everything from £80 corded electric models to £400 petrol classics.",
    longCopy: [
      "Push mowers come in three power types: manual reel (zero emissions, zero noise), corded electric (cheap and quiet), and petrol push (the most powerful for lawns up to 500 m²). All three skip the drive system, keeping cost, weight and maintenance low.",
      "For lawns up to 100 m², a manual or corded electric push mower is unbeatable on value and ease of use. For 100–500 m², a 40–46 cm petrol push mower with a 140–160cc engine is the sweet spot.",
      "Look for a single-lever central height adjuster, a 45+ litre grass box, and a folding handle for easier storage. Mulching capability is a useful bonus for hot, dry summer mowing.",
    ],
    faqs: [
      {
        q: "How big a lawn can a push mower handle?",
        a: "A push petrol mower with a 40–46 cm deck handles lawns up to about 500 m² comfortably. Corded electric and manual push mowers are best for 100–200 m².",
      },
      {
        q: "Is a push mower hard work?",
        a: "On flat lawns under 400 m², no — modern push mowers roll easily and weigh 20–30 kg. On slopes or anything over 400 m², a self-propelled mower will save you a lot of effort.",
      },
      {
        q: "Are manual push reel mowers any good?",
        a: "For small, well-maintained lawns up to 100 m², yes — a quality manual reel mower from Brill, Webb or Allett gives a precise cut, zero emissions and no fuel or charging at all.",
      },
    ],
    filter: m => m.mower_type === "push" && m.self_propelled !== true,
  },
};

export const lawnMowerSubcategoryList: LawnMowerSubcategoryConfig[] = Object.values(lawnMowerSubcategories);
